import { createApiClient } from "@esi/api/client";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import { api, apiFetch } from "../api.ts";

/**
 * The backoffice's API: the tenant admin's own (`/api/admin`), or, for an operator under a
 * support grant, the same routes mounted read-only at `/api/operator/support/<grant>/admin`
 * (spec `support-access`). Every backoffice screen reads through this, so one set of screens
 * serves both.
 */
export interface AdminApi {
  admin: typeof api.api.admin;
  /** Under a support grant: write actions are hidden (the server refuses them with 403). */
  readOnly: boolean;
  /** Where the backoffice's screens live in the SPA (`/backoffice`, or under the console). */
  base: string;
}

export const BACKOFFICE_BASE = "/backoffice";

const AdminApiContext = createContext<AdminApi>({
  admin: api.api.admin,
  readOnly: false,
  base: BACKOFFICE_BASE,
});

export function useAdminApi(): AdminApi {
  return useContext(AdminApiContext);
}

/** Rewrites `/api/admin/…` to the grant's support mount; every other path is left alone. */
export function supportPath(path: string, grantId: string): string {
  return path.replace(
    /^\/api\/admin(?=\/|$|\?)/,
    `/api/operator/support/${encodeURIComponent(grantId)}/admin`,
  );
}

/** The backoffice of a tenant, read-only, through the operator's own support grant. */
export function SupportAdminApiProvider(props: {
  grantId: string;
  base: string;
  children: ReactNode;
}) {
  const value = useMemo<AdminApi>(() => {
    const fetchUnder = ((input: RequestInfo | URL, init?: RequestInit) => {
      const url = new URL(
        input instanceof Request ? input.url : String(input),
        window.location.href,
      );
      url.pathname = supportPath(url.pathname, props.grantId);
      return apiFetch(url.pathname + url.search, init);
    }) as typeof fetch;
    return {
      admin: createApiClient("/", { fetch: fetchUnder }).api.admin,
      readOnly: true,
      base: props.base,
    };
  }, [props.grantId, props.base]);
  return <AdminApiContext.Provider value={value}>{props.children}</AdminApiContext.Provider>;
}
